import dataApi from './dataApi'

// Title list items don't include credits, so each title card asks for its title's details
// once it scrolls into view. Results are cached for the session, cards showing the same
// title share one request, and at most MAX_CONCURRENT detail requests run at a time.
// A queued request that no card is waiting for anymore is dropped before it starts.
const MAX_CONCURRENT = 4
// Cards show fewer than this; the rest of each list is not kept.
const MAX_NAMES_KEPT = 3

const cache = new Map() // ncTitleId -> { directorNameList, actorNameList }
const pending = new Map() // ncTitleId -> { promise, resolve, waiters }
const queue = []
let running = 0

const keepNames = (list) => (Array.isArray(list) ? list.slice(0, MAX_NAMES_KEPT) : [])

async function fetchCredits(ncTitleId) {
  try {
    const response = await dataApi.getTitleForCredits(ncTitleId)
    const title = response?.data
    if (!title) return null
    return {
      directorNameList: keepNames(title.directorNameList),
      actorNameList: keepNames(title.actorNameList),
    }
  } catch (error) {
    console.error(`Failed to fetch credits for title ${ncTitleId}:`, error)
    return null
  }
}

function pump() {
  while (running < MAX_CONCURRENT && queue.length > 0) {
    const ncTitleId = queue.shift()
    const entry = pending.get(ncTitleId)
    if (!entry) continue
    if (entry.waiters === 0) {
      pending.delete(ncTitleId)
      entry.resolve(null)
      continue
    }
    running++
    fetchCredits(ncTitleId)
      .then((credits) => {
        // Failures aren't cached, so a later request can try again.
        if (credits) cache.set(ncTitleId, credits)
        entry.resolve(credits)
      })
      .finally(() => {
        pending.delete(ncTitleId)
        running--
        pump()
      })
  }
}

export function getCachedCredits(ncTitleId) {
  return cache.get(ncTitleId) || null
}

/** Resolves to { directorNameList, actorNameList }, or null if unavailable or dropped. */
export function requestCredits(ncTitleId) {
  if (cache.has(ncTitleId)) return Promise.resolve(cache.get(ncTitleId))

  let entry = pending.get(ncTitleId)
  const isNew = !entry
  if (isNew) {
    let resolve
    const promise = new Promise((r) => (resolve = r))
    entry = { promise, resolve, waiters: 0 }
    pending.set(ncTitleId, entry)
  }
  entry.waiters++
  if (isNew) {
    queue.push(ncTitleId)
    pump()
  }
  return entry.promise
}

/** Call when a card no longer needs a credits request it made (scrolled away or destroyed). */
export function releaseCredits(ncTitleId) {
  const entry = pending.get(ncTitleId)
  if (entry && entry.waiters > 0) entry.waiters--
}
